import React from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import {
	Timeline,
	TimelineConnector,
	TimelineContent,
	TimelineDot,
	TimelineItem,
	TimelineOppositeContent,
	timelineOppositeContentClasses
} from '@mui/lab';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { EducationHistoryEntry } from '../../../types/user';
import {
	deriveMostRecentSchool,
	EducationJurisdiction,
	institutionTypeLabel,
	resolveEducationJurisdiction,
	sortMostRecentFirst
} from '../../../utils/educationHistory';

/**
 * Education-history timeline (PTEC-517) — the single rendering of the
 * canonical list, most recent first, with the DERIVED "most recent school
 * attended" highlighted (it is the only school detail Lead-Share could ever
 * disclose). Read-only on the profile view; the editor passes `onRemove` to
 * add delete controls. Type labels localise to the viewer's jurisdiction;
 * stored values stay canonical.
 */
export const EducationTimeline = ({
	entries,
	jurisdiction = 'INTL',
	onRemove
}: {
	entries: EducationHistoryEntry[];
	jurisdiction?: EducationJurisdiction;
	onRemove?: (entry: EducationHistoryEntry) => void;
}) => {
	const mostRecent = deriveMostRecentSchool(entries);
	const ordered = sortMostRecentFirst(entries);

	if (ordered.length === 0) {
		return null;
	}

	return (
		<Timeline
			data-testid="education-timeline"
			sx={{
				p: 0,
				m: 0,
				[`& .${timelineOppositeContentClasses.root}`]: { flex: 0.18, pl: 0 }
			}}
		>
			{ordered.map((entry, index) => {
				const isMostRecent = entry === mostRecent;
				return (
					<TimelineItem
						key={`${entry.institution}-${entry.endYear ?? 'ongoing'}-${index}`}
						data-testid={`education-item-${index}`}
					>
						<TimelineOppositeContent sx={{ pt: 1.1 }}>
							<Typography variant="caption" sx={{ fontWeight: isMostRecent ? 700 : 400 }}>
								{entry.endYear ?? 'Ongoing'}
							</Typography>
						</TimelineOppositeContent>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<TimelineDot color={isMostRecent ? 'primary' : 'grey'} sx={{ my: 0.75 }}>
								<SchoolRounded sx={{ fontSize: 14 }} />
							</TimelineDot>
							{index < ordered.length - 1 && <TimelineConnector />}
						</Box>
						<TimelineContent sx={{ pb: 2 }}>
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: isMostRecent ? 700 : 500 }}>
										{entry.institution}
										{isMostRecent && (
											<Chip
												size="small"
												label="Most recent"
												color="primary"
												sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
											/>
										)}
									</Typography>
									{entry.type && (
										<Typography variant="caption" sx={{ color: 'var(--gray-1)' }}>
											{institutionTypeLabel(
												entry.type,
												entry.country
													? resolveEducationJurisdiction({ isoCountryCode: entry.country })
													: jurisdiction
											)}
										</Typography>
									)}
								</Box>
								{onRemove && (
									<IconButton
										size="small"
										aria-label={`Remove ${entry.institution}`}
										onClick={() => onRemove(entry)}
									>
										<DeleteOutlineRounded fontSize="small" />
									</IconButton>
								)}
							</Stack>
						</TimelineContent>
					</TimelineItem>
				);
			})}
		</Timeline>
	);
};

export default EducationTimeline;
